import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface FooterProps {
  lang?: 'zh' | 'en';
}

const Footer: React.FC<FooterProps> = ({ lang = 'zh' }) => {
  const { t } = useTranslation('website');

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  return (
    <footer className="py-16 border-t border-dark-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-10 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src="/images/logo-128.png" alt="FancyPeter" className="w-8 h-8 rounded-lg object-contain" />
              <span className="text-white font-bold text-xl">FancyPeter</span>
            </div>
            <p className="text-dark-500 text-sm" style={{ lineHeight: '1.8' }}>{t('footer.brandDesc')}</p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">{t('footer.quickLinks')}</h4>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'about', label: t('nav.about') },
                { id: 'business', label: t('nav.business') },
                { id: 'tech', label: t('nav.tech') },
                { id: 'cases', label: t('nav.cases') },
                { id: 'philosophy', label: t('nav.philosophy') },
                { id: 'vision', label: t('nav.vision') },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="text-dark-500 hover:text-primary-400 text-sm transition-colors text-left"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-4">{t('footer.contactTitle')}</h4>
            <div className="space-y-2">
              <a href="mailto:fancyai4u@outlook.com" className="flex items-center gap-2 text-dark-500 hover:text-primary-400 text-sm transition-colors">
                <i data-lucide="mail" style={{ width: 14, height: 14 }} /> fancyai4u@outlook.com
              </a>
              <div className="flex items-center gap-2 text-dark-500 text-sm">
                <i data-lucide="map-pin" style={{ width: 14, height: 14 }} /> {lang === 'zh' ? '广东省' : 'Guangdong, China'}
              </div>
              <a href="https://www.fancypeter.com" target="_blank" rel="noopener" className="flex items-center gap-2 text-dark-500 hover:text-primary-400 text-sm transition-colors">
                <i data-lucide="globe" style={{ width: 14, height: 14 }} /> www.fancypeter.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-dark-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-dark-600 text-xs">
            &copy; 2025 FancyPeter. All rights reserved. Guangdong FancyPeter Technology Co., Ltd.{' '}
            <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener" className="hover:text-primary-400 transition-colors">
              {t('footer.icp')}
            </a>
          </span>
          <Link
            to={lang === 'zh' ? '/en' : '/'}
            className="text-dark-500 hover:text-primary-400 text-xs transition-colors flex items-center gap-1"
          >
            <i data-lucide="languages" style={{ width: 12, height: 12 }} />
            {t('footer.switchToEn')}
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
